import { motion, AnimatePresence, type Variants } from 'framer-motion'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useAppDispatch } from '../../store'
import { setToken, setUser } from '../../store/slices/authSlice'
import api from '@/api/apiClient'
import type { AuthResponse } from '@/models/auth'
import { useTranslation } from '@/hooks/useTranslation'

const authCardVariants: Variants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1, transition: { type: 'spring' as const, duration: 0.5 } },
}

const spinnerVariants: Variants = {
  animate: { rotate: 360, transition: { duration: 1, repeat: Infinity, ease: 'linear' as const } },
}

const checkmarkVariants: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { type: 'spring' as const, duration: 0.8, bounce: 0 },
      opacity: { duration: 0.2 },
    },
  },
}

const errorVariants: Variants = {
  shake: { x: [-10, 10, -10, 10, 0], transition: { duration: 0.5 } },
}

type State = 'loading' | 'success' | 'error'

export default function AuthPage() {
  const { t } = useTranslation()
  const [state, setState] = useState<State>('loading')
  const [message, setMessage] = useState<string>(t('pages.authPage.validatingToken'))
  const search = useSearch({ from: '/auth' })
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Track tokens to avoid duplicate validations: tokens currently in progress and completed
  const inProgressTokensRef = useRef<Set<string>>(new Set())
  const completedTokensRef = useRef<Set<string>>(new Set())

  const validateTokenAndRedirect = useCallback(async (token: string) => {
    // Prevent duplicate calls for the same token
    if (completedTokensRef.current.has(token)) {
      setMessage(t('pages.authPage.tokenAlreadyValidated'))
      setState('success')
      return
    }
    if (inProgressTokensRef.current.has(token)) {
      // Another validation is already running for this token
      return
    }

    inProgressTokensRef.current.add(token)

    setState('loading')
    setMessage(t('pages.authPage.validatingToken'))

    try {
      const { data } = await api.post<AuthResponse>('/auth/telegram', { token })
      const res = data

      if (res?.success && res?.data?.token) {
        // Save to localStorage
        try {
          localStorage.setItem('token', res.data.token)
          localStorage.setItem('user', JSON.stringify(res.data.user ?? {}))
        } catch (err) {
          console.warn('Failed to save auth to localStorage', err)
        }

        // Update Redux store
        dispatch(setToken(res.data.token))
        dispatch(setUser(res.data.user))

        // Mark token as completed so it won't be re-validated
        completedTokensRef.current.add(token)

        // Update UI state
        setState('success')
        setMessage(res.message || 'Authentication successful!')

        // Navigate to dashboard after showing success
        setTimeout(() => {
          navigate({ to: '/dashboard/services/my-services', replace: true })
        }, 2000)
      } else {
        setState('error')
        setMessage(t('pages.authPage.authenticationFailed'))
      }
    } catch (e: unknown) {
      let msg = t('pages.authPage.validationFailed')
      if (e instanceof Error) {
        msg = e.message
      }
      console.error('Token validation error:', msg)
      setState('error')
      setMessage(msg)
    } finally {
      inProgressTokensRef.current.delete(token)
    }
  }, [dispatch, navigate])

  useEffect(() => {
    const token = (search as { token?: string }).token
    if (!token) {
      setState('error')
      setMessage(t('pages.authPage.missingToken'))
      return
    }

    validateTokenAndRedirect(token)
  }, [search, validateTokenAndRedirect])

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <motion.div initial="initial" animate="animate" variants={authCardVariants} className="w-full max-w-md rounded-lg border bg-card p-8 text-center">
        <AnimatePresence mode="wait">
          {state === 'loading' && (
            <motion.div key="loading" className="flex flex-col items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div className="size-12 rounded-full border-4 border-muted border-t-primary" variants={spinnerVariants} animate="animate" />
              <p className="text-sm text-muted-foreground">{message}</p>
            </motion.div>
          )}

          {state === 'success' && (
            <motion.div key="success" className="flex flex-col items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                <motion.path variants={checkmarkVariants} initial="hidden" animate="visible" d="M20 6L9 17l-5-5" />
              </svg>
              <p className="text-sm font-medium text-green-600">{message}</p>
              <p className="text-xs text-muted-foreground">{t('pages.authPage.redirectingToDashboard')}</p>
            </motion.div>
          )}

          {state === 'error' && (
            <motion.div key="error" className="flex flex-col items-center gap-4" variants={errorVariants} animate="shake">
              <div className="size-12 rounded-full border-4 border-destructive flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </div>
              <p className="text-sm text-destructive">{message}</p>
              <button 
                onClick={() => {
                  const token = (search as { token?: string }).token
                  if (token) {
                    validateTokenAndRedirect(token)
                  }
                }} 
                className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('pages.authPage.retry')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}
